import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import KillsHeader from './KillsHeader';
import KillsTable from './KillsTable';
import KillModal from './KillModal';
import HoldsKillsChart from './HoldsKillsChart';

const Kills = ({
  csrfToken,
  admissionSeatingChartEvent,
  addOnSeatingChartEvent,
  seatingChartEvents,
  kills,
  fetchKills,
  createKill,
  updateKill,
  deleteKill,
  reloadTicketTypes,
  reloadAddOns
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingKill, setEditingKill] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const isLegacy = selectedEvent?.legacy;
  const eventCloneMissing = !selectedEvent?.event_chart_clone_id;

  useEffect(() => {
    const available = [admissionSeatingChartEvent, addOnSeatingChartEvent, ...(seatingChartEvents || [])].filter(Boolean);
    if (!selectedEvent || !available.find(e => e.id === selectedEvent.id)) {
      setSelectedEvent(available[0] || null);
    }
  }, [admissionSeatingChartEvent?.id, addOnSeatingChartEvent?.id, (seatingChartEvents || []).map(e => e.id).join(',')]);

  useEffect(() => {
    if (!selectedEvent) return undefined;
    const controller = new AbortController();
    fetchKills(csrfToken, selectedEvent, controller.signal);
    return () => controller.abort();
  }, [selectedEvent?.id]);

  const allEvents = [admissionSeatingChartEvent, addOnSeatingChartEvent, ...(seatingChartEvents || [])].filter(Boolean);

  if (!allEvents.length) {
    return (
      <div className="text-center my-5">Set up a seating chart to manage Kills.</div>
    );
  }

  if (isLegacy) {
    return (
      <div className="text-center my-5">Kills are not supported for legacy events.</div>
    );
  }

  if (eventCloneMissing) {
    return (
      <div className="text-center my-5">Kills are not supported for events without an event chart. Please reselect a chart to access Kills.</div>
    );
  }

  return (
    <div>
      <KillsHeader
        events={allEvents}
        selectedEvent={selectedEvent}
        onSelectEvent={(value) => {
          const ev = allEvents.find(evt => String(evt.id) === String(value));
          setSelectedEvent(ev || null);
        }}
        onClickAdd={() => {
          setEditingKill(null);
          setShowModal(true);
        }}
      />

      <KillsTable
        kills={kills}
        onEdit={(kill) => {
          setEditingKill(kill);
          setShowModal(true);
        }}
        onDelete={(kill) => {
          deleteKill(csrfToken, selectedEvent, kill).then(() => {
            if (selectedEvent?.context === 'admission') {
              reloadTicketTypes();
            } else {
              reloadAddOns();
            }
          });
        }}
      />

      <HoldsKillsChart selectedEvent={selectedEvent} />

      <KillModal
        open={showModal}
        onClose={() => { setEditingKill(null); setShowModal(false); }}
        title={editingKill ? 'Edit Kill' : 'Add Kill'}
        editorKeyUrl={`/seating_charts/${selectedEvent?.event_chart_clone_id}/editor_key`}
        selectedEvent={selectedEvent}
        initialKill={editingKill ? {
          name: editingKill.name || '',
          objects: editingKill.objects || [],
          area_places: editingKill.area_places || {}
        } : null}
        onSubmit={(payload) => {
          const action = editingKill
            ? updateKill(csrfToken, { id: editingKill.id, ...payload })
            : createKill(csrfToken, selectedEvent, payload);
          return action.then(() => {
            if (selectedEvent?.context === 'admission') {
              reloadTicketTypes();
            } else {
              reloadAddOns();
            }
            setEditingKill(null);
            setShowModal(false);
          });
        }}
      />
    </div>
  );
};

Kills.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  admissionSeatingChartEvent: PropTypes.object,
  addOnSeatingChartEvent: PropTypes.object,
  seatingChartEvents: PropTypes.array,
  kills: PropTypes.array.isRequired,
  fetchKills: PropTypes.func.isRequired,
  createKill: PropTypes.func.isRequired,
  updateKill: PropTypes.func.isRequired,
  deleteKill: PropTypes.func.isRequired,
  reloadTicketTypes: PropTypes.func.isRequired,
  reloadAddOns: PropTypes.func.isRequired
};

export default Kills;
